// Mock data services for frontend-first development
// These will be replaced with real API calls in Epic 41

export const mockPrayerTimes = {
  city: 'Cairo',
  date: new Date(),
  prayers: [
    { name: 'fajr', adhan: '04:34', iqama: '04:44' },
    { name: 'dhuhr', adhan: '13:02', iqama: '13:15' },
    { name: 'asr', adhan: '16:38', iqama: '16:50' },
    { name: 'maghrib', adhan: '19:50', iqama: '20:10' },
    { name: 'isha', adhan: '21:17', iqama: '21:30' },
  ],
  nextPrayer: { name: 'dhuhr', time: '13:02', countdown: '3h 2min' },
}

export const generatePrayerCalendar = (days: number = 30) => {
  const calendar = []
  const today = new Date()
  
  for (let i = 0; i < days; i++) {
    const date = new Date(today)
    date.setDate(today.getDate() + i)
    
    calendar.push({
      date,
      prayers: mockPrayerTimes.prayers.map((p) => ({
        name: p.name,
        time: p.adhan,
      })),
    })
  }
  
  return calendar
}

export const mockCharities = [
  {
    id: 1,
    name: 'جمعية رسالة للأعمال الخيرية',
    registry: '9598',
    address: 'السيدة زينب، القاهرة',
    logo: '/logos/resala.png',
    category: 'zakat' as const,
  },
  {
    id: 2,
    name: 'مؤسسة مصر الخير',
    registry: '9597',
    address: 'السيدة زينب، القاهرة',
    logo: '/logos/misr-el-kheir.png',
    category: 'sadaqah' as const,
  },
  {
    id: 3,
    name: 'مؤسسة مستشفى أورام الأطفال',
    registry: '57357',
    address: 'السيدة زينب، القاهرة',
    logo: '/logos/57357.png',
    category: 'orphans' as const,
  },
  {
    id: 4,
    name: 'جمعية بنك الطعام المصري',
    registry: '9595',
    address: 'السيدة زينب، القاهرة',
    logo: '/logos/food-bank.png',
    category: 'sadaqah' as const,
  },
]

// Mock surah data with ayahs
export const mockSurahData = {
  1: {
    surah: { id: 1, number: 1, name: 'الفاتحة', nameEn: 'Al-Fatiha', ayahs: 7, revelationOrder: 5, juz: 1 },
    ayahs: [
      {
        number: 1,
        arabicText: 'بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ',
        translations: {
          en: 'In the name of Allah, the Entirely Merciful, the Especially Merciful.',
          fr: 'Au nom d\'Allah, le Tout Miséricordieux, le Très Miséricordieux.',
        },
      },
      {
        number: 2,
        arabicText: 'الْحَمْدُ لِلَّهِ رَبِّ الْعَالَمِينَ',
        translations: {
          en: '[All] praise is [due] to Allah, Lord of the worlds',
          fr: '[Toute] louange [appartient] à Allah, Seigneur des mondes',
        },
      },
      {
        number: 3,
        arabicText: 'الرَّحْمَٰنِ الرَّحِيمِ',
        translations: {
          en: 'The Entirely Merciful, the Especially Merciful,',
          fr: 'Le Tout Miséricordieux, le Très Miséricordieux,',
        },
      },
      {
        number: 4,
        arabicText: 'مَالِكِ يَوْمِ الدِّينِ',
        translations: {
          en: 'Sovereign of the Day of Recompense.',
          fr: 'Souverain du Jour de la Rétribution.',
        },
      },
      {
        number: 5,
        arabicText: 'إِيَّاكَ نَعْبُدُ وَإِيَّاكَ نَسْتَعِينُ',
        translations: {
          en: 'It is You we worship and You we ask for help.',
          fr: 'C\'est Toi [Seul] que nous adorons, et c\'est Toi [Seul] dont nous implorons secours.',
        },
      },
      {
        number: 6,
        arabicText: 'اهْدِنَا الصِّرَاطَ الْمُسْتَقِيمَ',
        translations: {
          en: 'Guide us to the straight path -',
          fr: 'Guide-nous dans le droit chemin,',
        },
      },
      {
        number: 7,
        arabicText: 'صِرَاطَ الَّذِينَ أَنْعَمْتَ عَلَيْهِمْ غَيْرِ الْمَغْضُوبِ عَلَيْهِمْ وَلَا الضَّالِّينَ',
        translations: {
          en: 'The path of those upon whom You have bestowed favor, not of those who have evoked [Your] anger or of those who are astray.',
          fr: 'Le chemin de ceux auxquels Tu as accordé Tes faveurs, non celui de ceux qui ont encouru Ta colère, ni celui des égarés.',
        },
      },
    ],
    reciters: [
      { id: 1, name: 'عبد الباسط عبد الصمد', audioUrl: 'https://example.com/audio/fatiha-abdul-basit.mp3' },
      { id: 2, name: 'محمد صديق المنشاوي', audioUrl: 'https://example.com/audio/fatiha-minshawi.mp3' },
    ],
  },
  2: {
    surah: { id: 2, number: 2, name: 'البقرة', nameEn: 'Al-Baqarah', ayahs: 286, revelationOrder: 87, juz: 1 },
    ayahs: [
      {
        number: 1,
        arabicText: 'الم',
        translations: {
          en: 'Alif, Lam, Meem.',
          fr: 'Alif, Lâm, Mîm.',
        },
      },
      {
        number: 2,
        arabicText: 'ذَٰلِكَ الْكِتَابُ لَا رَيْبَ ۛ فِيهِ ۛ هُدًى لِّلْمُتَّقِينَ',
        translations: {
          en: 'This is the Book about which there is no doubt, a guidance for those conscious of Allah -',
          fr: 'C\'est le Livre au sujet duquel il n\'y a aucun doute, c\'est un guide pour les pieux,',
        },
      },
    ],
    reciters: [
      { id: 1, name: 'عبد الباسط عبد الصمد', audioUrl: 'https://example.com/audio/baqarah-abdul-basit.mp3' },
    ],
  },
}

export const mockSurahs = [
  { id: 1, number: 1, name: 'الفاتحة', nameEn: 'Al-Fatiha', ayahs: 7, revelationOrder: 5, juz: 1 },
  { id: 2, number: 2, name: 'البقرة', nameEn: 'Al-Baqarah', ayahs: 286, revelationOrder: 87, juz: 1 },
  { id: 3, number: 3, name: 'آل عمران', nameEn: 'Aal Imran', ayahs: 200, revelationOrder: 89, juz: 3 },
  { id: 4, number: 4, name: 'النساء', nameEn: 'An-Nisa', ayahs: 176, revelationOrder: 92, juz: 4 },
  { id: 5, number: 5, name: 'المائدة', nameEn: 'Al-Maidah', ayahs: 120, revelationOrder: 112, juz: 6 },
  { id: 6, number: 6, name: 'الأنعام', nameEn: 'Al-Anam', ayahs: 165, revelationOrder: 55, juz: 7 },
  { id: 7, number: 7, name: 'الأعراف', nameEn: 'Al-Araf', ayahs: 206, revelationOrder: 39, juz: 8 },
  { id: 8, number: 8, name: 'الأنفال', nameEn: 'Al-Anfal', ayahs: 75, revelationOrder: 88, juz: 9 },
  { id: 9, number: 9, name: 'التوبة', nameEn: 'At-Tawbah', ayahs: 129, revelationOrder: 113, juz: 10 },
  { id: 10, number: 10, name: 'يونس', nameEn: 'Yunus', ayahs: 109, revelationOrder: 51, juz: 11 },
  { id: 11, number: 11, name: 'هود', nameEn: 'Hud', ayahs: 123, revelationOrder: 52, juz: 11 },
  { id: 12, number: 12, name: 'يوسف', nameEn: 'Yusuf', ayahs: 111, revelationOrder: 53, juz: 12 },
]

export const mockCategories = [
  { id: 'taharah', name: 'الطهارة', nameEn: 'Taharah' },
  { id: 'salah', name: 'الصلاة', nameEn: 'Salah' },
  { id: 'zakat', name: 'الزكاة', nameEn: 'Zakat' },
  { id: 'sawm', name: 'الصوم', nameEn: 'Sawm' },
  { id: 'hajj', name: 'الحج', nameEn: 'Hajj' },
]

export const mockBooks = [
  {
    id: 1,
    title: 'كتاب الطهارة',
    author: 'ابن قدامة',
    category: 'taharah',
    categoryName: 'الطهارة',
    chapters: 10,
    description: 'كتاب شامل في أحكام الطهارة من وضوء وغسل وتيمم',
  },
  {
    id: 2,
    title: 'كتاب الصلاة',
    author: 'ابن قدامة',
    category: 'salah',
    categoryName: 'الصلاة',
    chapters: 15,
    description: 'كتاب شامل في أحكام الصلاة وأوقاتها وشروطها',
  },
  {
    id: 3,
    title: 'كتاب الزكاة',
    author: 'ابن قدامة',
    category: 'zakat',
    categoryName: 'الزكاة',
    chapters: 8,
    description: 'كتاب شامل في أحكام الزكاة ونصابها ومصارفها',
  },
  {
    id: 4,
    title: 'كتاب الصوم',
    author: 'ابن قدامة',
    category: 'sawm',
    categoryName: 'الصوم',
    chapters: 12,
    description: 'كتاب شامل في أحكام الصوم وأحكام رمضان',
  },
  {
    id: 5,
    title: 'كتاب الحج',
    author: 'ابن قدامة',
    category: 'hajj',
    categoryName: 'الحج',
    chapters: 20,
    description: 'كتاب شامل في أحكام الحج والعمرة',
  },
  {
    id: 6,
    title: 'أحكام المياه',
    category: 'taharah',
    categoryName: 'الطهارة',
    chapters: 5,
    description: 'شرح تفصيلي لأنواع المياه وأحكامها',
  },
]

// Mock library book detail data
export const mockLibraryBookData: Record<
  number,
  {
    book: {
      id: number
      title: string
      author?: string
      category: string
      categoryName: string
    }
    content: {
      introduction: string
      chapters: Array<{
        id: number
        title: string
        content: string
        evidences: Array<{
          id: number
          type: 'quran' | 'hadith'
          reference: string
          text: string
          surahId?: number
          ayahNumber?: number
        }>
      }>
      relatedTopics: Array<{
        id: number
        title: string
        bookId: number
      }>
    }
  }
> = {
  1: {
    book: {
      id: 1,
      title: 'كتاب الطهارة',
      author: 'ابن قدامة',
      category: 'taharah',
      categoryName: 'الطهارة',
    },
    content: {
      introduction:
        'الطهارة هي رفع الحدث أو إزالة النجس. والحدث ينقسم إلى حدث أصغر وحدث أكبر. والنجس هو ما يمنع الصلاة.',
      chapters: [
        {
          id: 1,
          title: 'باب المياه',
          content:
            'المياه تنقسم إلى طاهرة وطاهرة مطهرة. الطاهرة هي التي يجوز استعمالها في الطهارة، والطاهرة المطهرة هي التي يجوز استعمالها في رفع الحدث وإزالة النجس.',
          evidences: [
            {
              id: 1,
              type: 'quran' as const,
              reference: 'سورة المائدة، آية 6',
              text: 'وَإِن كُنتُم مَّرْضَىٰ أَوْ عَلَىٰ سَفَرٍ أَوْ جَاءَ أَحَدٌ مِّنكُم مِّنَ الْغَائِطِ أَوْ لَامَسْتُمُ النِّسَاءَ فَلَمْ تَجِدُوا مَاءً فَتَيَمَّمُوا صَعِيدًا طَيِّبًا',
              surahId: 5,
              ayahNumber: 6,
            },
            {
              id: 2,
              type: 'hadith' as const,
              reference: 'صحيح البخاري',
              text: 'الماء طهور لا ينجسه شيء',
            },
          ],
        },
        {
          id: 2,
          title: 'باب الوضوء',
          content:
            'الوضوء هو غسل أعضاء معينة بنية الطهارة. وهي: الوجه، واليدين إلى المرفقين، ومسح الرأس، والرجلين إلى الكعبين.',
          evidences: [
            {
              id: 3,
              type: 'quran' as const,
              reference: 'سورة المائدة، آية 6',
              text: 'يَا أَيُّهَا الَّذِينَ آمَنُوا إِذَا قُمْتُمْ إِلَى الصَّلَاةِ فَاغْسِلُوا وُجُوهَكُمْ وَأَيْدِيَكُمْ إِلَى الْمَرَافِقِ',
              surahId: 5,
              ayahNumber: 6,
            },
          ],
        },
      ],
      relatedTopics: [
        { id: 2, title: 'باب الوضوء', bookId: 1 },
        { id: 6, title: 'أحكام المياه', bookId: 1 },
      ],
    },
  },
  2: {
    book: {
      id: 2,
      title: 'كتاب الصلاة',
      author: 'ابن قدامة',
      category: 'salah',
      categoryName: 'الصلاة',
    },
    content: {
      introduction: 'الصلاة هي ركن من أركان الإسلام الخمسة، وهي فرض على كل مسلم بالغ عاقل.',
      chapters: [
        {
          id: 1,
          title: 'باب شروط الصلاة',
          content: 'شروط الصلاة هي: الإسلام، والعقل، والتمييز، ودخول الوقت، والطهارة من الحدث الأصغر والأكبر.',
          evidences: [],
        },
      ],
      relatedTopics: [],
    },
  },
}

// Mock videos data
export const mockVideos: Array<{
  id: number
  title: string
  thumbnail?: string
  duration: string
  views: number
  topic: 'prayer' | 'zakat' | 'wudu' | 'hajj' | 'sawm' | 'all'
  description?: string
}> = [
  {
    id: 1,
    title: 'كيفية الوضوء الصحيح',
    duration: '5:30',
    views: 125000,
    topic: 'wudu',
    description: 'شرح تفصيلي لكيفية الوضوء الصحيح وفق السنة النبوية',
  },
  {
    id: 2,
    title: 'أوقات الصلاة وفضلها',
    duration: '12:45',
    views: 89000,
    topic: 'prayer',
    description: 'تعرف على أوقات الصلاة وفضلها في الإسلام',
  },
  {
    id: 3,
    title: 'كيف تحسب زكاتك',
    duration: '8:20',
    views: 67000,
    topic: 'zakat',
    description: 'دليل شامل لحساب الزكاة بشكل صحيح',
  },
  {
    id: 4,
    title: 'مناسك الحج والعمرة',
    duration: '25:10',
    views: 145000,
    topic: 'hajj',
    description: 'شرح كامل لمناسك الحج والعمرة',
  },
  {
    id: 5,
    title: 'فضائل الصيام',
    duration: '15:30',
    views: 98000,
    topic: 'sawm',
    description: 'تعرف على فضائل الصيام في رمضان',
  },
  {
    id: 6,
    title: 'كيفية أداء صلاة الجمعة',
    duration: '7:15',
    views: 56000,
    topic: 'prayer',
    description: 'تعليم كيفية أداء صلاة الجمعة',
  },
  {
    id: 7,
    title: 'أنواع المياه في الطهارة',
    duration: '9:45',
    views: 42000,
    topic: 'wudu',
    description: 'شرح أنواع المياه المختلفة وأحكامها',
  },
  {
    id: 8,
    title: 'مصارف الزكاة الشرعية',
    duration: '11:20',
    views: 78000,
    topic: 'zakat',
    description: 'تعرف على مصارف الزكاة الشرعية',
  },
]

